import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { StoresApi } from "apis/StoresApi";
import { CommonColumnProperties, CustomColumns, FormInput, ImportBy, ImportWizard, ImportWizardRef, Modal, Spinner, Title } from "components";
import { ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { ZButton } from "components/ZButton";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZTextInput } from "components/ZTextInput";
import * as datefns from "date-fns";
import { ZIconType } from "helpers/utility/iconMap";
import { useProductFilter, useToggle } from "hooks";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { StoreExtended } from "models/dbModels/stores/Store";
import { SupplierNameMappingView } from "models/dbModels/supplierNameMappings/SupplierNameMappingView";
import { ManagedStocksPopup } from "popups/ManagedStocks/ManagedStocks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getSupplierNameMappings } from "redux/slices/SupplierNameMappings";
import { getSuppliers } from "redux/slices/Suppliers";
import { showToast } from "redux/slices/Toasts";
import { SupplierGroupDealImportWorkerData, SupplierGroupDealImportWorkerResponseData } from "./supplierGroupDealImportWorker";
import { ManagedSupplierGroupDealsApi } from "apis/ManagedSupplierGroupDealsApi";
import { ManagedSupplierGroupDealsTemplateView, ManagedSupplierGroupDealsStockView } from "models/dbModels/managedSupplierDeals/ManagedSupplierDealsTemplate";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ZNumberInput } from "components/ZNumberInput";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { ZIcon } from "components/ZIcon";
import { ManagedStockGroupStock } from "models/dbModels/managedStockGroups/ManagedStockGroupStock";
import { SearchState } from "components/AgGrid/FilterComponents/Search";
import { FilterState } from "components/AgGrid/FilterComponents/Filters";
import { AgGridToolbar } from "components/AgGrid/AgGridToolbar";
import { ZTooltip } from "components/ZTooltip";
import { RootState } from "redux/store";
import { Modal as BSModal } from "react-bootstrap";
import { BaseColDefParams } from "ag-grid-community/dist/lib/entities/colDef";
import { v4 as uuidv4 } from 'uuid';
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { AgGridCustomColumn } from "components/AgGrid/AgCustomColumnTemplate";

export interface DealTermModel {
	moq: number;
	discount: number;
}

export function GetTermsDescription(terms: DealTermModel[], minOrderType: string): string {
	return terms.map((r) => `${minOrderType === "C" ? "$" : ""}${r.moq}${minOrderType === "U" ? " units" : ""} for ${r.discount}%`).join(" | ");
}

interface CurrentModel extends ManagedSupplierGroupDealsStockView {
	hasError?: { [key in keyof ManagedSupplierGroupDealsStockView]?: boolean };
	customId?: string;
}


export function NewSupplierGroupDeal() {
	const getProductFilterOptions = useProductFilter();
	const user = useAppSelector((state: RootState) => state.logedinUser);	
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const importWizardRef = useRef<ImportWizardRef>(null);
	const [showImport, setShowImport] = useState<boolean>(false);
	const [showAdd, setShowAdd] = useState<boolean>(false);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const { modelId } = useParams();

	const [stores, setStores] = useState<StoreExtended[]>([]);
	const [suppliers, setSuppliers] = useState<ManagedSupplier[]>([]);

	const [isNew, setIsNew] = useState<boolean>(true);
	const [isValidated, setValidated] = useToggle();
	const form = useRef<HTMLFormElement>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const createOrderAfterSave = useRef<boolean>(false);
	const stockTableRef = useRef<AgGridReact>(null);
	const termsTableRef = useRef<AgGridReact>(null);
	const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false);
	const [selected, setSelected] = useState<number[]>([]);
	const [bulkOptions, setBulkOptions] = useState<{
		moq: number | null;
		notes: string | null;
	}>({
		moq: null,
		notes: null,
	});

	const [showTermsDialog, setShowTermsDialog] = useState<boolean>(false);

	const [supplierGroupDeal, setSupplierGroupDeal] = useState<ManagedSupplierGroupDealsTemplateView>({
		id: 0,
		name: "",
		managedSupplierID: 0,
		notes: "",
		endDate: null,
		minOrder: 0,
		minOrderType: "U",
		startDate: null,
		stocks: [],
		stores: [],
		stockCount: 0,
		storeCount: 0,
		terms: null,
	});

	const [supplierGroupDealStock, setSupplierGroupDealStock] = useState<ManagedSupplierGroupDealsStockView[]>([]);

	const [dealTerms, setDealTerms] = useState<DealTermModel[]>([]);
	const gridSearch = useRef<{ searchState?: SearchState; filterState?: FilterState }>({});

	
	const [totalRows, setTotalRows] = useState<number>(0);
	const [hasErrors, setHasErrors] = useState<boolean>(false);

	const canEdit = (modelId && user.permissions.supplierGroupDeals.canUpdate) ||
					(!modelId && user.permissions.supplierGroupDeals.canCreate);

	useEffect(() => {
		if (modelId && !isNaN(Number.parseInt(modelId))) {
			setLoading(true);
			//Load the existing Stock deal
			ManagedSupplierGroupDealsApi.Search({ ids: [Number.parseInt(modelId)], loadDetails: ["Stores"] })
				.then(async (res) => {
					if (res.data.data.length === 1) {
						setSupplierGroupDeal(res.data.data[0]);

						const fetchStocks = async () => {
							const stocks: ManagedSupplierGroupDealsStockView[] = [];
							let hasStock = true;
							let offset = 0;
						
							while (hasStock) {
								const stockRes = await ManagedSupplierGroupDealsApi.SearchStock({ supplierGroupDealIds: [res.data.data[0].id], limit: 500, offset: offset });
								if (stockRes.data.data.length === 0 || stockRes.data.data.length < 500) {
									hasStock = false;
								} 
								
								stocks.push(...stockRes.data.data.map((s) => ({ ...s, customId: uuidv4(), hasError: {} })));
								offset += 500;
							}						
							return stocks;
						};
						
						const stocks = await fetchStocks();


						setTotalRows(stocks.length);
						setSupplierGroupDealStock(stocks);					
						setDealTerms(res.data.data.length > 0 && res.data.data[0].terms != null ? JSON.parse(res.data.data[0].terms) : []);
						setIsNew(false);
						setLoading(false);
					} else {
						navigate("/app/supplier-assorted-deals/new");
						setLoading(false);
					}
				})
				.catch(() => {
					navigate("/app/supplier-assorted-deals/new");
					setLoading(false);
				});
		}
	}, [modelId]);

	//Load the models into states
	useEffect(() => {
		dispatch(getSuppliers()).then((res) => {
			setSuppliers(res.data);
		});

		StoresApi.Search({}).then((res) => {
			setStores(res.data.data);
		});
	}, []);

	
	const stockColumns = useMemo(() => {
		var columns: ColDef<CurrentModel>[] = [
			{
				headerName: "Name",
				sortable: true,
				field: "name",
				minWidth: 110,
				flex: 1,
				sort: "asc",
				filter: "agTextColumnFilter",
				sortIndex: 0,
				checkboxSelection: true,
				headerCheckboxSelection: true,
				headerCheckboxSelectionFilteredOnly: true,
				headerCheckboxSelectionCurrentPageOnly: false,
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					if (params.data.hasError!["name"] === true) {
						const errorText: string[] = [];
						if (params.data.hasError!["name"]) {
							errorText.push("Duplicate line");
						}
						return {
							decorators: [
									{
										decorator: <ZTooltip text={errorText.join(", ")}><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
					}				
				},
			},
			{
				headerName: "MOQ",
				field: "moq",
				sortable: true,
				minWidth: 80,
				cellRenderer: "IntegerCellRenderer",
				editable: canEdit ? true : false,
				valueSetter: (params) => {
					const parsed = parseInt(params.newValue);
					params.data.moq = isNaN(parsed) ? null : parsed;
					return true;
				},
				headerTooltip: "Minimum Order Quantity",
			},
			{
				headerName: "Notes",
				field: "notes",
				sortable: true,
				minWidth: 110,
				flex: 1,
				editable: canEdit ? true : false,
			},
			{
				headerName: "Error", 
				field: "hasError",
				valueGetter: (params) => {
					const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
					return anyHasError ? "Error" : "";
				},
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
					if (anyHasError) {

						return {
							decorators: [
									{
										decorator: <ZTooltip text="Row has errors, please correct to save."><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
						}
				},
			},
		];

		//Fix the sorting number placement
		columns.forEach((col) => {
			col.headerComponentParams = {template: AgGridCustomColumn }
		});

		return columns;
	}, []);
	
	const dealTermColumns: ColDef<DealTermModel>[] = [
		{
			headerName: supplierGroupDeal.minOrderType === "U" ? "Units" : "Dollars",
			sortable: true,
			field: "moq",
			minWidth: 110,
			flex: 1,
			sort: "asc",
			editable: true,
			sortIndex: 0,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			headerCheckboxSelectionCurrentPageOnly: false,
			valueSetter: (params) => {
				const parsed = parseInt(params.newValue);
				params.data.moq = isNaN(parsed) ? 0 : parsed;
				return true;
			},
		},
		{
			headerName: "Percent Discount",
			field: "discount",
			sortable: true,
			minWidth: 80,
			cellRenderer: "IntegerCellRenderer",
			editable: true,
			valueSetter: (params) => {
				const parsed = parseFloat(params.newValue);
				params.data.discount = isNaN(parsed) ? 0 : parsed;
				return true;
			},
		},
	];

	const importWizardExtraMatches: CustomColumns[] = [
		{
			title: "MOQ",
			key: "Moq",
			required: false,
			display: true,
			validation: (value: any) => {
				if (value !== "" && isNaN(value)) {
					return { valid: false, error: "MOQ must be a number" };
				}
				if (value !== "" && parseFloat(value) < 0) {
					return { valid: false, error: "MOQ must be a positive number" };
				}
				return { valid: true, error: "" };
			},
			autoMatch: ["moq", "qty"],
		},
		{
			title: "Notes",
			key: "Notes",
			required: false,
			display: true,
			validation: (value: any) => {
				return { valid: true, error: "" };
			},
		},
	];


	function WholeTablePostProcess(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		const itemQtySet = new Set<string>();

		rows.forEach((row) => {
			if (row.hasError === undefined || row.hasError === false) {
				//don"t bother validating bad rows
				let errors: string[] = [];

				if (row.stockId != null && row.stockId !== "") {
					const checkSet = row.stockId + "|" + row.Qty;
					if (itemQtySet.has(checkSet)) {
						errors.push("Duplicate item and quantity");
					} else {
						itemQtySet.add(checkSet);
					}
				} else {
					errors.push("No Match");
				}

				if (errors.length > 0) {
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					row.message = [...(row.message == null || row.message === "" ? [] : [row.message]), ...errors].join(", ");
				}
			}
		});
		return temp;
	}


	function getAgGridRows(): ManagedSupplierGroupDealsStockView[] {
		const rows: ManagedSupplierGroupDealsStockView[] = [];
		stockTableRef.current?.api.forEachNode((node) => rows.push(node.data));
		return rows;
	}

	const terminateWorkersRef = useRef<Function>();
	const [importPercentage, setImportPercentage] = useState<number>(0);
	async function importStockAsync(rows: any[]) {
		const webWorkerData: SupplierGroupDealImportWorkerData = {
			data: rows,
			stockList: [...getAgGridRows()],
		};

		pauseValidation.current = true;

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<SupplierGroupDealImportWorkerData, any, SupplierGroupDealImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("./supplierGroupDealImportWorker.ts", import.meta.url), { type: "module" }),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				// There's no http call in this web worker but should we keep this in case? Probably not... hmm...
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.__name);
							errors.set(r.__name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				} else {
					const updatedStocks: CurrentModel[] = responseContext.response.data.updatedStocks.map((y) => {
						return ({...y, customId: uuidv4(), hasError: {}});
					});

					stockTableRef.current?.api.applyTransaction({
						add: updatedStocks,
					});
				}

				setImportPercentage((prev) => Math.min(100, prev + (1 / responseContext.numBatches) * 100));
			},
			onWorkersFinished: async (_) => setImportPercentage(0),
			onWorkerError: (error, unprocessedData) => {
				unprocessedData.forEach((data) => {
					const errorEntry = errors.get(data.__name);
					const errorMessage = error.message ?? "Unknown error occurred";
					errors.set(data.__name ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
				});
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});


		pauseValidation.current = false;
		agGridTableValidation();

		return [...returnErrors];
	}

	function handleSubmit() {
		setSaving(true);
		if (form.current?.checkValidity() === true) {

			const sendModel: ManagedSupplierGroupDealsTemplateView = {
				...supplierGroupDeal,
				stocks: getAgGridRows(),
				terms: JSON.stringify(dealTerms),
			};

			if (isNew) {
				ManagedSupplierGroupDealsApi.Add([sendModel]).then((res) => {
					if (res.data.data.length > 0) {
						if (createOrderAfterSave.current) {
							navigate("/app/ordering/new?supplierGroupDeal=" + res.data.data[0]);
						} else {
							navigate("/app/supplier-assorted-deals/" + res.data.data[0]);
						}
					} else {
						dispatch(showToast({ value: ["Failed to add Assorted Deal"] }));
					}
					setSaving(false);
				}).catch(() => setSaving(false));
			} else {
				ManagedSupplierGroupDealsApi.Update([sendModel]).then((res) => {
					if (res.data.data) {
						if (createOrderAfterSave.current) {
							navigate("/app/ordering/new?supplierGroupDeal=" + modelId);
						} else {
							navigate("/app/supplier-assorted-deals/" + modelId);
						}
					} else {
						dispatch(showToast({ value: ["Failed to update Assorted Deal"] }));
					}
					setSaving(false);
				}).catch(() => setSaving(false));
			}
		}
		setShowConfirm(false);
		setValidated();
	}

	async function addStock() {
		if(stocks.selectAllFilter != true){
			const selectedIds = stocks.selectedRows[0].map(x => x.id);
			const stocksToAdd = stocks.table.filter(x=> selectedIds.includes(x.id));
			processAddedManageStockData(stocksToAdd);
			setShowAdd(false);
			return;
		}
		//Load all managed stock
		ManagedStocksApi.OptimsedSearch({
			keyword: stocks.keyword,
			searchPdes: stocks.searchPdes,
			searchBarcodes: stocks.searchBarcodes,
			offset: null,
			limit: null,
			supplierIds: stocks.supplierIdFilter == undefined? []: [stocks.supplierIdFilter],
			departmentIds: stocks.departmentIdFilter == undefined? []: [stocks.departmentIdFilter],
			subdepartmentIds: stocks.subdepartmentIdFilter == undefined? []: [stocks.subdepartmentIdFilter],
			manufacturerIds: stocks.manufacturerIdFilter == undefined? []: [stocks.manufacturerIdFilter],
			stockGroupIds: stocks.stockGroupIdFilter == undefined? []: [stocks.stockGroupIdFilter],
			active: stocks.activeFilter,
			ethical: stocks.ethicalFilter,
			schedule: stocks.scheduleFilter,
			pbs: stocks.pbsFilter
		})
			.then((res) => {
				const currentIds = getAgGridRows().map((r) => r.managedStockId);
				const newStocks: CurrentModel[] = res.data.data
					.filter((z) => !currentIds.includes(z.id))
					.map((r) => ({
						managedStockId: r.id,
						name: r.name,
						managedSupplierGroupDealId: 0,
						moq: null,
						notes: null,
						hasError: {},
						customId: uuidv4(),
					}));

				stockTableRef.current?.api.applyTransaction({ add: newStocks });
			})
			.finally(() => {
				setShowAdd(false);
			});
	}

	function processAddedManageStockData(managedStock: any[]){
		const currentIds = getAgGridRows().map((r) => r.managedStockId);
		const newStocks = managedStock.filter((z) => !currentIds.includes(z.id))
		.map((r) => ({
			managedStockId: r.id,
			name: r.name,
			managedSupplierGroupDealId: 0,
			moq: null,
			notes: null,
			hasError: {},
			customId: uuidv4(),
		}));
		stockTableRef.current?.api.applyTransaction({ add: newStocks });		
	};

	const runningValidation = useRef(false);
	const pauseValidation = useRef(false);
	function agGridTableValidation() {

		if (runningValidation.current)
			return;

		if (pauseValidation.current)
			return;

		runningValidation.current = true;

		console.log("agGridTableValidation")
		var hasChange = false;
		var hasError = false;


		if (supplierGroupDeal.name === "") {
			hasError = true;
		}

		if (supplierGroupDeal.managedSupplierID === 0) {
			hasError = true;
		}

		if (supplierGroupDeal.endDate != null && supplierGroupDeal.startDate != null && datefns.isAfter(new Date(supplierGroupDeal.startDate), new Date(supplierGroupDeal.endDate))) {
			hasError = true;
		}

		
		const key = (row: CurrentModel) => row.managedStockId;
		const stocks: CurrentModel[] = getAgGridRows();
		setTotalRows(stocks.length);

		const groupedStocks = stocks.reduce((acc, stock) => {
			const stockKey = key(stock);
			if (!acc[stockKey]) {
				acc[stockKey] = [];
			}
			acc[stockKey].push(stock);
			return acc;
		}, {} as { [key: string]: CurrentModel[] });

		stocks.forEach((stock: CurrentModel) => {
			hasChange = false;
			const isDuplicate = groupedStocks[key(stock)]?.some((x: CurrentModel) => x.customId !== stock.customId) ?? false;
			if (isDuplicate !== (stock.hasError?.name === true)) {
				stock.hasError = { ...stock.hasError, name: isDuplicate };
				hasChange = true;
			}
			
			hasError = hasError || (stock.hasError?.name === true )

			if (hasChange) {
				const node = stockTableRef.current?.api.getRowNode(stock.customId ?? "");
				if (node) {
					node.setData({ ...stock});
				}
			}
		});

		setHasErrors(hasError);

		runningValidation.current = false;
	}




	return (
		<>
			<Title pageTitle={isNew ? "New Assorted Deal" : "Update Assorted Deal"} crumbs={[{ name: "Assorted Deals", path: "/app/supplier-assorted-deals" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
										<Row>
											<Col md={4}>
												<ZTextInput
													label="Deal Name:"
													type="text"
													placeholder="Enter assorted deal name"
													value={supplierGroupDeal.name}
													key="text"
													onChange={(e) => {
														setSupplierGroupDeal((prev) => ({
															...prev,
															name: e,
														}));
														setHasErrors(e === "" || e === undefined);
													} }
													validation={{
														invalid: supplierGroupDeal.name === "",
														error: "Name is required",
													}}
													disabled={!canEdit}
												/>
											</Col>
											<Col md={4}>
												<ZSingleSelectDropdown<ManagedSupplier>
													containerClassName="w-100"
													label="Supplier:"
													placeholder="Select Supplier"
													options={suppliers}
													value={suppliers.find((s) => s.id === supplierGroupDeal.managedSupplierID)}
													display={(s) => s.name}
													disabled={(s) => false}
													onSelectionChange={(opt) => setSupplierGroupDeal((prev) => ({ ...prev, managedSupplierID: opt?.id ?? 0 }))}
													hasSearch
													searchPlaceholder="Search Suppliers"
													optionKey={function (option: ManagedSupplier) {
														return option.id;
													}}
													validation={{
														invalid: supplierGroupDeal.managedSupplierID === 0 || supplierGroupDeal.managedSupplierID == null,
														error: "Supplier is required",
													}}
													dropdownIsDisabled={!canEdit}
												/>
											</Col>
											<Col md={4}>
												<ZMultiSelectDropdown<StoreExtended>
													containerClassName="w-100"
													label="Stores:"
													placeholder="Select stores"
													values={stores.filter((s) => supplierGroupDeal.stores.includes(s.id))}
													options={stores}
													display={(s) => s.name}
													value={(s) => s.id}
													minMenuWidthPx={300}
													disabled={(s) => false}
													onSelectionChange={(opt) => setSupplierGroupDeal((prev) => ({ ...prev, stores: opt.map((o) => o.id) }))}
													hasAllOption
													hasSearch
													searchPlaceholder="Search stores"
													dropdownIsDisabled={!canEdit}
												/>
											</Col>
										</Row>

										<Row className="mt-1">

											<Col md={4}>
												<ZTextInput
													label="Deal Notes:"
													type="text"
													placeholder="Enter notes"
													value={supplierGroupDeal.notes ?? ""}
													key="text"
													onChange={(e) =>
														setSupplierGroupDeal((prev) => ({
															...prev,
															notes: e,
														}))
													}
													disabled={!canEdit}
												/>
												
											</Col>

											<Col md={4}>
												<ZDateTimePicker
													label="Start Date"
													value={supplierGroupDeal.startDate != null ? new Date(supplierGroupDeal.startDate) : null}
													onChanged={(date) => {
														setSupplierGroupDeal((prev) => ({
															...prev,
															startDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
														}));
													}}
													disabled={!canEdit}
													error={supplierGroupDeal.startDate != null && supplierGroupDeal.endDate != null && datefns.isAfter(new Date(supplierGroupDeal.startDate), new Date(supplierGroupDeal.endDate)) ? "Start date cannot be after end date" : undefined}
												/>
											</Col>
											<Col md={4}>
												<ZDateTimePicker
													label="End Date"
													value={supplierGroupDeal.endDate != null ? new Date(supplierGroupDeal.endDate) : null}
													onChanged={(date) => {
														setSupplierGroupDeal((prev) => ({
															...prev,
															endDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
														}));
													}}
													disabled={!canEdit}
													error={supplierGroupDeal.startDate != null && supplierGroupDeal.endDate != null && datefns.isAfter(new Date(supplierGroupDeal.startDate), new Date(supplierGroupDeal.endDate)) ? "End date cannot be before start date" : undefined}
												/>
											</Col>
										</Row>

										<Row className="mt-1">
											<Col md={8}>

												<ZTextInput
													label="Deal Terms:"
													type="text"
													placeholder="Edit to add terms"
													value={GetTermsDescription(dealTerms, supplierGroupDeal.minOrderType)}
													key="text"	
													style={{caretColor: 'transparent'}}
													endAdornment={<span onClick={() => canEdit && setShowTermsDialog(true)}> <ZIcon icon={ZIconType.Edit} size="14px" style={{cursor: 'pointer'}}/> </span>}
													disabled={!canEdit}
													/>

												{/* <ZNumberInput
													label="Min Order Value:"
													placeholder="Enter Assorted deal minimum order value"
													value={supplierGroupDeal.minOrder}
													key="text"
													onChange={(e) =>
														setSupplierGroupDeal((prev) => ({
															...prev,
															minOrder: e ?? 0,
														}))
													}
													validation={{
														invalid: supplierGroupDeal.minOrder < 0,
														error: "Min Order is required",
													}}
													type={supplierGroupDeal.minOrderType === "U" ? "qty" : "price"}
												/> */}
											</Col>
											<Col md={4}>
												<ZSingleSelectDropdown<string>
													containerClassName="w-100"
													label="Term Type (Units vs Dollars):"
													placeholder="Select Order Type"
													options={["Dollars", "Units"]}
													value={supplierGroupDeal.minOrderType === "U" ? "Units" : "Dollars"}
													disabled={(s) => false}
													onSelectionChange={(opt) => setSupplierGroupDeal((prev) => ({ ...prev, minOrderType: opt === "Units" ? "U" : "C" }))}
													hasSearch
													searchPlaceholder="Search Order Type"
													optionKey={(s) => s}
													display={(s) => s}
													dropdownIsDisabled={!canEdit}
												/>
											</Col>

										</Row>

										<AgGridToolbar 
												search={true}
												className="my-1 px-2 pt-2"
												handleSubmit={(searchState, filterState) => {
													gridSearch.current = { searchState, filterState };
													console.log(stockTableRef.current?.api.getFilterModel());
													if (searchState?.keyword === "") {
														stockTableRef.current?.api.setFilterModel({});
														stockTableRef.current?.api.onFilterChanged();
													} else {
														stockTableRef.current?.api.setFilterModel({
															name: {
																filterType: "text",
																type: searchState?.contains === true ?  "contains" : "startsWith",
																filter: searchState?.keyword ?? ""
															}
														});
														stockTableRef.current?.api.onFilterChanged();
													}
												}}
												searchProps={
													{totalRows: totalRows, placeholder: "by Name", hideContains: false}
												}
												children={(<div className="d-inline-flex gap-1">
				
				
													<ZTooltip enabled={canEdit} text={canEdit ? "Add" : "You do not have permission to add"}>
														<ZButton buttonSize="md" icon={ZIconType.Add} variant="light" onClick={() => setShowAdd(true)} 
														className={!canEdit ? "invisible" : ""} />
													</ZTooltip>

													<ZTooltip enabled={canEdit} text={canEdit ? "Bulk Edit" : "You do not have permission to bulk edit"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Edit}
															disabled={selected.length === 0}
															className={!canEdit ? "invisible" : ""}
															variant="light"
															onClick={() => {
																setShowBulkEdit(true);
															}}
														/>
													</ZTooltip>


													<ZTooltip enabled={canEdit} text={canEdit ? "Delete" : "You do not have permission to delete"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Delete}
															variant="light"
															onClick={() => {
																//Remove selected rows
																const selectedRows = stockTableRef.current?.api.getSelectedRows();
																if (selectedRows) {
																	stockTableRef.current?.api.applyTransaction({ remove: selectedRows });
																}
															}}
															disabled={selected.length === 0}
															className={!canEdit ? "invisible" : ""}
														/>
													</ZTooltip>
				
													<ZTooltip enabled={canEdit} text={canEdit && user.permissions.supplierGroupDeals.canImport ? "Import" : "You do not have permission to import"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Upload}
															variant="light"
															onClick={() => {
																setShowImport(true);
															}}
															disabled={!canEdit}
															className={!user.permissions.supplierGroupDeals.canImport ? "invisible" : ""}
														/>
													</ZTooltip>
				
												</div>
													
												)}
											/> 

										<Row>
											<AgGridReact<CurrentModel>
												ref={stockTableRef}
												suppressNoRowsOverlay={loading}
												columnDefs={stockColumns}
												rowData={supplierGroupDealStock}
												getRowId={(p) => p.data.customId ?? ""}
												rowSelection="multiple"
												className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
												containerStyle={{ height: "56dvh" }}
												suppressRowClickSelection
												singleClickEdit
												rowMultiSelectWithClick
												groupAllowUnbalanced
												animateRows
												suppressContextMenu
												editType="fullRow"
												stopEditingWhenCellsLoseFocus={true}
												enterNavigatesVerticallyAfterEdit={true}
												onSelectionChanged={(e) => {
													setSelected(e.api.getSelectedRows().map((r: CurrentModel) => r.managedStockId));
												}}
												gridOptions={{
													rowClassRules: {
														"ag-row-error": (params) => {
															const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
															return anyHasError;
														}
													},
												}}
												components={{
													baseCellRenderer: BaseCellRenderer,
												}}
												onRowDataUpdated={() => {
													agGridTableValidation();
												}}
												onRowValueChanged={() => {
													agGridTableValidation();
												}}
											/>
										</Row>

										<Row className="mt-2">
											<Col sm={6}>
												<Link to="/app/supplier-assorted-deals" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
													<i className="mdi mdi-arrow-left"></i> Back to Assorted Deals
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">

												<ZTooltip text={hasErrors && canEdit ? "Please ensure all fields are valid before saving" : (
																!canEdit ? "You do not have permission to edit" : "") }
																enabled={hasErrors || !canEdit}>													
													<ZButton
														variant="primary"
														className="btn cta-button"
														disabled={saving || loading || hasErrors || !canEdit} 
														onClick={() => {															
															if (form.current?.checkValidity() === true) {
																stockTableRef.current?.api.stopEditing();
																setShowConfirm(true);
															}														
														}}
													>
														{saving ? (
															<>
																<Spinner className="spinner-border-sm me-1" tag="span" color="white" />
																Saving...
															</>
														) : (
															<>Save</>
														)}
													</ZButton>
												</ZTooltip>

												{/* <Button
													variant="secondary"
													className="btn cta-button me-2"
													disabled={loading}
													onClick={() => {
														if (form.current?.checkValidity() === true) {
															stockTableRef.current?.api.stopEditing();
															createOrderAfterSave.current = true;
															setShowConfirm(true);
														}
													}}
												>
													Create Order
												</Button> */}
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>


			
			<BSModal
				centered
				size={"sm"}
				show={loading}
				keyboard={false}
				animation={true}
				backdrop={"static"}
				
			>
				<BSModal.Dialog className="m-0">
					<BSModal.Body>
						<div style={{textAlign: 'center'}}>Loading</div>
					</BSModal.Body>
				</BSModal.Dialog>
			</BSModal>

			<ManagedStocksPopup show={showAdd} onClose={() => setShowAdd(false)} onSave={addStock} />

			<ImportWizard
				ref={importWizardRef}
				title="Assorted Deals"
				display={showImport}
				matchAgainst={"Stocks"}
				customColumns={importWizardExtraMatches}
				postProcessStep={(rows) => WholeTablePostProcess(rows)}
				finish={() => setShowImport(false)}
				importPercentage={importPercentage}
				handleClose={() => {
					if (terminateWorkersRef.current) terminateWorkersRef.current();
					setShowImport(false);
				}}
				importAsync={(i) => importStockAsync(i)}
			/>

			<Modal
				title="Confirm"
				show={showConfirm}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				handleSubmit={handleSubmit}
				handleClose={() => setShowConfirm(false)}
				description={
					<>
						<p>You are about to sync the Assorted Deal down to the selected store(s).</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>

			<Modal
				title="Set Deal Terms"
				show={showTermsDialog}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				size="lg"
				handleSubmit={() => {
					let terms: DealTermModel[] = [];
					termsTableRef.current?.api.stopEditing();
					termsTableRef.current?.api.forEachNode((node) => {
						terms.push(node.data);
					});
					
					setDealTerms(terms);	
					setShowTermsDialog(false);				
				}}
				handleClose={() => setShowTermsDialog(false)}
				description={
					<>
						<Row className="mt-3 mb-2">
							<Col xs={3} className="d-flex align-items-center"></Col>
							<Col xs={9} className="d-flex justify-content-end gap-2 align-items-end">
							<ZButton
								buttonSize="sm"
								icon={ZIconType.Add}
								variant="light"
								onClick={() => {
									termsTableRef.current?.api.applyTransaction({ add: [{ moq: 1, discount: 0 }] });
								}}
							/>
								<ZButton
								buttonSize="sm"
								icon={ZIconType.Delete}
								variant="light"
								onClick={() => {
									const selectedRows = termsTableRef.current?.api.getSelectedRows();
									if (selectedRows) {
										termsTableRef.current?.api.applyTransaction({ remove: selectedRows });
									}
								}}
							/>
							</Col>
						</Row>

						<AgGridReact
							ref={termsTableRef}
							suppressNoRowsOverlay={loading}
							columnDefs={dealTermColumns}
							rowData={dealTerms}
							//getRowId={(p: GetRowIdParams<PdeRowData, any>) => p.data.rowId}
							rowSelection="multiple"
							className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
							containerStyle={{ height: "55vh" }}
							suppressRowClickSelection
							singleClickEdit
							rowMultiSelectWithClick
							groupAllowUnbalanced
							animateRows
							suppressContextMenu
							editType="fullRow"
							stopEditingWhenCellsLoseFocus={true}
							enterNavigatesVerticallyAfterEdit={true}
						/>
					</>
				}
			/>

						<Modal
							title="Bulk Edit"
							show={showBulkEdit}
							buttonType="primary"
							buttonText={"Continue"}
							buttonCloseText="Cancel"
							handleSubmit={() => {
								const selectedRows = stockTableRef.current?.api.getSelectedRows();
								if (selectedRows) {
									selectedRows.forEach((r) => {
										if (bulkOptions.moq != null) r.moq = bulkOptions.moq;
										if (bulkOptions.notes != null) r.notes = bulkOptions.notes;
									});
								}
			
								stockTableRef.current?.api.refreshCells();
								
								setShowBulkEdit(false);
								setBulkOptions({
									moq:null,
									notes: null,
								});
							}}
							handleClose={() => setShowBulkEdit(false)}
							description={
								<>
									<Row>
										<Col sm={12}>
											<ZNumberInput
												label="MOQ:"
												placeholder="Enter Minumum Order Quantity"
												value={bulkOptions.moq}
												key="text"
												onChange={(e) =>
													setBulkOptions((prev) => ({
														...prev,
														moq: e === undefined ? null : e,
													}))
												}
												type={"qty"}
											/>
										</Col>
									</Row>									
									<Row>
										<Col sm={12}>
											<ZTextInput
												label="Notes:"
												type="text"
												placeholder="Enter notes"
												value={bulkOptions.notes ?? ""}
												key="text"
												onChange={(e) =>
													setBulkOptions((prev) => ({
														...prev,
														notes: e,
													}))
												}
											/>
										</Col>
									</Row>
								</>
							}
						/>
		</>
	);
}
